import * as DatasetHelper from '../../utils/dataset-helper';
import HistoricalWeightingCalculator from '../../utils/calculators/historical-weighting-calculator';
import FlexibleCalculator from '../../utils/calculators/flexible-calculator';

const TEMPLATE = `
    <table class="table" id="calculations">
        <thead class="thead-light">
            <tr>
                <th scope="col"></th>
                <th scope="col">Criteria</th>
                <th scope="col">Score</th>
                <th scope="col">Weight</th>
                <th scope="col">Weighted Score</th>
            </tr>
        </thead>
        <tbody>
        </tbody>
        <tfoot class="thead-light">
            <tr id="criteria-row-combined" >
                <th colspan="3"><strong>Combined</strong></th>
                <th class="criteria-weight"></th>
                <th class="criteria-score-total"></th>
            </tr>
        </tfoot>
    </table>
    <div class="buttons"><button type="button" class="btn btn-primary download">Download results</button>'</div>
`;

export default class CalculationsPanelComponent {
  /**
   * Constructor
   * @param $container
   * @param datasetsModel
   * @param variablesModel
   * @param resampledDataModel
   * @param referenceSitesHashModel
   * @param selectedSitesModel
   */
  constructor($container, datasetsModel, variablesModel, resampledDataModel, referenceSitesHashModel, selectedSitesModel) {
    this._$container = $container;
    this._datasetsModel = datasetsModel;
    this._variablesModel = variablesModel;
    this._referenceSitesHashModel = referenceSitesHashModel;
    this._selectedSitesModel = selectedSitesModel;
    this._flexibleCalculator = new FlexibleCalculator();
    this._historicalWeightingCalculator = new HistoricalWeightingCalculator();

    this.combinedWeight = 0.0;
    this.combinedScore = 0.0;

    // Render the template.
    this._$container.html(TEMPLATE);

    // Capture static element references.
    this._$tbody = this._$container.find('tbody');
    this._$tfoot = this._$container.find('tfoot');

    const downloadButton = this._$container.find('div.buttons button.download');
    downloadButton.click(this._downloadResults.bind(this));

    resampledDataModel.on(resampledDataModel.EVENT_DATA_CHANGED, this.handleChange.bind(this));
  }

  /**
   * The underlying score has changed, so re-render the table.
   *
   * @private
   */
  handleChange(event) {
    this.combinedWeight = 0.0;
    this.combinedScore = 0.0;

    this._renderTableContent();
  }

  _renderTableContent() {
    this._variablesModel.getItems()
      .filter(variable => {
        return variable.isNumerical && !variable.excludeFromCalculations;
      })
      .forEach((variable) => {
        this._createRow(
          'spatialReconstruction',
          variable.id,
          variable.title,
          'Spatial reconstruction with mean squared error',
          variable.meanSquaredError || 0.0,
          variable.defaultWeight
        );
      });

    const activeDataset = this._datasetsModel.getActiveDataset();
    const selectedSiteIds = Object.keys(this._selectedSitesModel.getData());

    // Variances.
    if (activeDataset.calculations.categoryFairness) {
      activeDataset.calculations.categoryFairness
        .forEach(calculationDefn => {
          const result = this._flexibleCalculator.calculate(
            calculationDefn,
            activeDataset,
            this._referenceSitesHashModel.getData(),
            selectedSiteIds
          );
          this._createRow(
            'categoryFairness',
            calculationDefn.id,
            calculationDefn.title,
            calculationDefn.description || 'Category fairness with variance',
            result || 0.0,
            calculationDefn.defaultWeight || 0.1
          );
        });
    }

    // Historical Weightings.
    if (activeDataset.calculations.historicalWeighting) {
      activeDataset.calculations.historicalWeighting
        .forEach(calculationDefn => {
          const result = this._historicalWeightingCalculator.calculate(
            calculationDefn,
            activeDataset,
            this._referenceSitesHashModel.getData(),
            selectedSiteIds
          );
          const fieldDefn = calculationDefn.field;
          this._createRow(
            'historicalWeighting',
            calculationDefn.id,
            calculationDefn.title,
            'Score based on the number of years of historic data',
            result || 0.0,
            calculationDefn.defaultWeight || 0.1
          );
        });
    }

    // Update the footer.
    this._$tfoot.find('th.criteria-weight').html(this.combinedWeight.toFixed(4));
    this._$tfoot.find('th.criteria-score-total').html(this.combinedScore.toFixed(4));
  }

  /**
   * Create a row for a criteria
   * @param type
   * @param name
   * @param id
   * @param description
   * @param score
   * @param defaultWeight
   * @return {string}
   * @private
   */
  _createRow(type, id, name, description, score, defaultWeight) {
    let $row = this._$tbody.find(`tr[data-aims-criteria-key="${id}"]`);
    if ($row.length === 0) {
      $row = $(`<tr data-aims-criteria-key="${id}" data-type="${type}" data-name="${name}"></tr>`);
      this._$tbody.append($row);
    }

    let checked = '';
    if ($row.find('td.criteria-active input').length) {
      checked = $row.find('td.criteria-active input:checked').length === 1 ? 'checked' : '';
    }
    const weight = parseFloat($row.find('td.criteria-weight input').val()) || defaultWeight;
    let rowTotal = 0.0;
    if (checked) {
      rowTotal = (score * weight);
      this.combinedWeight += weight;
      this.combinedScore += rowTotal;
    }

    $row.html(`
          <td class="criteria-active">
              <input type="checkbox" value="${id}" ${checked}>
          </td>
          <th scope="row">${name}<br><span class="description">${description}</span></th>
          <td class="criteria-score">${score.toFixed(4)}</td>
          <td class="criteria-weight">
              <input type="text" value="${weight}" title="Weight for ${name}">
          </td>
          <td class="criteria-score-total">${rowTotal.toFixed(4)}</td>
    `);

    $row.find('input').on('change', (event) => this.handleChange(event));
  }

  /**
   * Trigger the download of the result set
   * @private
   */
  _downloadResults() {
    // Add settings to result CSV
    let resultString =
      'Active,' +
      'Criteria,' +
      'Score,' +
      'Weight,' +
      'Weighted Score,\n';


    this._$tbody.find('tr').each((index, row) => {
      const $row = $(row);
      const active = $row.find('td.criteria-active input').is(':checked');
      const id = $row.find('td.criteria-active input').val();
      const score = $row.find('td.criteria-score').html();
      const weight = active ? $row.find('td.criteria-weight input').val() : 0;
      const weightedScore = $row.find('td.criteria-score-total').html();

      resultString +=
        active + ',' +
        id + ',' +
        score + ',' +
        weight + ',' +
        weightedScore + ',\n'
    });

    resultString +=
      'Combined,,,' +
      this._$tfoot.find('th.criteria-weight').html() + ',' +
      this._$tfoot.find('th.criteria-score-total').html() + ',\n';

    resultString += '\n\n';
    resultString += 'Algorithm,\n';
    resultString += $('#algorithm-selector').val() + ',\n';

    resultString += '\n\n';
    resultString += 'Sites,\n';
    resultString += Object.keys(this._selectedSitesModel.getData()).join(',\n');

    const filename = "calculations-results.csv";
    const file = new Blob([resultString], {type: "text/plain;charset=utf-8"});
    if (window.navigator.msSaveOrOpenBlob) // IE10+
      window.navigator.msSaveOrOpenBlob(file, filename);
    else { // Others
      const a = document.createElement("a"),
        url = URL.createObjectURL(file);
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(function () {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 0);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/calculations-panel/component.js